import type { SyncStatus } from '../data/sync';
import { timeAgoShort } from './dates';

/** What the top-bar sync pill shows. The pill shares its row with the page title and the page's
 *  buttons, so `text` stays short enough that titles never truncate; `label` is the full wording
 *  for screen readers. */
export interface SyncPill {
  text: string;
  label: string;
  state: string;
}

/** Age of the last sync as short as it gets: "now" / "3m" / "3h" / "2d". */
const ageShort = (iso: string, now: Date): string => {
  const s = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 1000));
  if (s < 45) return 'now';
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m`;
  if (s < 86_400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86_400)}d`;
};

export interface SyncHeadline {
  text: string;
  tone: string;
}

const STATE_TEXT: Record<SyncStatus['state'], string> = {
  idle: 'Up to date',
  syncing: 'Syncing…',
  offline: 'Offline — changes are queued on this phone',
  error: 'Sync error — will retry automatically',
  unauthorized: 'Not signed in',
};

/** What the Sync screen's status card says. Same priorities as the pill: a rejected change and a
 *  phone that never synced are not "Up to date". */
export const syncHeadline = (s: SyncStatus): SyncHeadline => {
  if (s.state === 'unauthorized' || s.state === 'syncing' || s.state === 'offline') {
    return { text: STATE_TEXT[s.state], tone: s.state };
  }
  if (s.parked) {
    return {
      text: `${s.parked} change${s.parked === 1 ? ' needs' : 's need'} draft recovery`,
      tone: 'error',
    };
  }
  if (s.failed) {
    return { text: `${s.failed} change${s.failed === 1 ? '' : 's'} rejected by the server`, tone: 'error' };
  }
  if (s.state === 'error') return { text: STATE_TEXT.error, tone: 'error' };
  if (!s.lastSync) return { text: 'Not synced yet', tone: 'idle' };
  return { text: STATE_TEXT.idle, tone: 'idle' };
};

export const syncPill = (s: SyncStatus, now: Date = new Date()): SyncPill => {
  const plain = (text: string, state: string): SyncPill => ({ text, label: text, state });
  if (s.state === 'unauthorized') return plain('Sign in', 'unauthorized');
  if (s.state === 'syncing') return plain('Syncing…', 'syncing');
  if (s.state === 'offline') {
    return s.pending
      ? { text: `Offline · ${s.pending}`, label: `Offline · ${s.pending} queued`, state: 'offline' }
      : plain('Offline', 'offline');
  }
  if (s.parked) return plain(`${s.parked} parked`, 'error');
  if (s.failed) return plain(`${s.failed} failed`, 'error');
  if (s.state === 'error') return plain(s.pending ? `Retrying · ${s.pending}` : 'Sync error', 'error');
  if (s.pending) return plain(`${s.pending} pending`, 'pending');
  if (!s.lastSync) return plain('Not synced', 'idle');
  return {
    text: `Synced ${ageShort(s.lastSync, now)}`,
    label: `Synced ${timeAgoShort(s.lastSync, now)}`,
    state: 'idle',
  };
};
